import React from "react";

import { createGenerateClassName, StylesProvider } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import { Button } from "../../../../../../../../../components/Form";
import { Color } from "../../../../../../../../../components/Form/Button";
import UITabMaterial, {
  PanelProps,
} from "../../../../../../../../../components/Generic/UITabMaterial";
import { useActions } from "../../hooks/actions";
import { AvailabilityTab } from "../Tabs/Availability";
import { CertificationsTab } from "../Tabs/Certifications";
import { DetailsTab } from "../Tabs/Details";
import { TimesheetTab } from "../Tabs/Timesheet";
import { Footer, UITabWrapper } from "./styles";
import { useAppState } from "Context/StateContext";
import { IconTimezoneClock } from "Components/Generic/Icons/IconTimezoneClock";
import { colors } from "@zapalacorp/tokens";
import { Box } from "@mui/material";
import { formatGMT } from "Util/Format";

interface EditComponentProps {
  isLoading?: boolean;
  isUpdating?: boolean;
  errors?: yup.ValidationError[];
  onSubmit: () => void;
}

const memberTimesheetGenerated = createGenerateClassName({
  productionPrefix: "mbrtms-",
  seed: "membertimesheet",
});

export const EditComponent: React.FC<EditComponentProps> = (props) => {
  const { t: translate } = useTranslation();
  const { location } = useHistory<{ isView?: boolean }>();
  const { onBackToMembersTab, member } = useActions();
  const { onSubmit, isUpdating, isLoading, errors = [] } = props;
  const { userTimezone } = useAppState();

  const personalDetails: PanelProps = {
    key: "details",
    menuItem: translate("details"),
    className: "p-0",
    render: () => <DetailsTab isLoading={isLoading} errors={errors} />,
  };

  const timesheetPanel: PanelProps = {
    key: "timesheet",
    menuItem: translate("timesheet"),
    render: () => (
      <StylesProvider generateClassName={memberTimesheetGenerated}>
        <TimesheetTab />
      </StylesProvider>
    ),
  };

  const availabilitiesPanel: PanelProps = {
    key: "availability",
    menuItem: translate("availability"),
    render: () => <AvailabilityTab />,
  };

  const certificationsPanel: PanelProps = {
    key: "certifications",
    menuItem: translate("certifications"),
    render: () => <CertificationsTab memberId={member?.id!} />,
  };

  const formatTimezoneDisplay = (timezone: any) => {
    if (!timezone?.gmtOffset || !timezone?.nameFormatted) {
      return `${timezone?.name || "Unknown"} (GMT ${timezone?.gmtOffset || 0})`;
    }

    return `GMT (${formatGMT(timezone.gmtOffset)}) ${timezone.nameFormatted
      ?.replace("/", " / ")
      .replace("_", "")}`;
  };

  return (
    <UITabWrapper>
      <UITabMaterial
        useInitialPadding
        leftPanels={[personalDetails, timesheetPanel, availabilitiesPanel, certificationsPanel]}
        rightComponentClasses="d-flex align-items-center justify-content-center"
        rightComponent={() => (
          <Box
            letterSpacing={0.5}
            fontFamily="Manrope"
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1}
            color={colors.gray500}
          >
            <IconTimezoneClock color={colors.gray500} />
            <span>{formatTimezoneDisplay(userTimezone)}</span>
          </Box>
        )}
      />

      {!location?.state?.isView && (
        <Footer>
          <Button customColor={Color.TEXT_INFO} onClick={onBackToMembersTab}>
            {translate("cancel")}
          </Button>
          <Button
            customColor={Color.SUCCESS}
            loading={isUpdating}
            disabled={isUpdating}
            onClick={onSubmit}
          >
            {translate("save")}
          </Button>
        </Footer>
      )}
    </UITabWrapper>
  );
};
